/* =========================================================================
 * shipping-picker.js  (v3 — Leaflet + InPost ShipX + lista + responsive)
 *
 * v3 zmiany:
 *  - Preload Leaflet od razu po wybraniu Paczkomatu (nie czekaj na button)
 *  - Sidebar lista najbliższych paczkomatów obok mapy
 *  - AbortController — anuluj poprzednie wyszukiwanie przy nowym
 *  - Skeleton loading state w liście
 *  - Cache wyników po query w sesji
 *  - Modal responsive z mapy invalidateSize na resize
 * ========================================================================= */
(function () {
  'use strict';

  const ATTR_METHOD = 'shipping_method';
  const ATTR_ID     = 'pickup_point_id';
  const ATTR_NAME   = 'pickup_point_name';
  const ATTR_ADDR   = 'pickup_point_address';

  const METHODS_NEED_POINT = ['paczkomat-inpost', 'poczta-odbior'];

  const SHIPX_API = 'https://api-shipx-pl.easypack24.net/v1';
  const LEAFLET = {
    js:  'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js',
    css: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css',
  };
  const POLAND_CENTER = [52.0697, 19.4805];
  const POLAND_ZOOM = 6;
  const SEARCH_RADIUS_M = 15000;
  const DEFAULT_QUERY = '02-747'; // okolica sprzedawcy (Tabula Rasa, Steinhausa 2/52)

  // ---------- helpers ----------

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function setStatus(root, msg, type = 'info') {
    const el = $('[data-status]', root);
    if (!el) return;
    if (!msg) { el.hidden = true; el.textContent = ''; return; }
    el.hidden = false;
    el.textContent = msg;
    el.dataset.statusType = type;
  }

  function loadCSS(href) {
    if ($(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function loadJS(src) {
    return new Promise((resolve, reject) => {
      if ($(`script[src="${src}"]`)) { resolve(); return; }
      const s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Nie udało się załadować ${src}`));
      document.head.appendChild(s);
    });
  }

  function isPolishPostCode(s) { return /^\d{2}-?\d{3}$/.test((s || '').trim()); }
  function normalizePostCode(s) {
    const t = (s || '').trim();
    if (/^\d{5}$/.test(t)) return t.slice(0, 2) + '-' + t.slice(2);
    return t;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function formatDistance(meters) {
    if (!meters || meters < 0) return '';
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  }

  // ---------- ShipX with cache + abort ----------

  const shipxCache = new Map();
  let activeShipxAbort = null;

  async function shipxSearch(params, cacheKey) {
    if (cacheKey && shipxCache.has(cacheKey)) return shipxCache.get(cacheKey);
    if (activeShipxAbort) activeShipxAbort.abort();
    activeShipxAbort = new AbortController();
    const qs = new URLSearchParams({
      type: 'parcel_locker',
      status: 'Operating',
      per_page: '50',
      ...params,
    });
    const r = await fetch(`${SHIPX_API}/points?${qs}`, { signal: activeShipxAbort.signal });
    if (!r.ok) throw new Error(`ShipX HTTP ${r.status}`);
    const data = await r.json();
    const items = data.items || [];
    if (cacheKey) shipxCache.set(cacheKey, items);
    return items;
  }

  // ---------- cart attributes ajax ----------

  async function pushAttributes(attrs) {
    const r = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    });
    if (!r.ok) throw new Error(`Cart update HTTP ${r.status}`);
    return r.json();
  }

  // ---------- UI sync ----------

  function syncHiddenInputs(root, state) {
    const map = {
      [ATTR_METHOD]: state.method || '',
      [ATTR_ID]:     state.pointId || '',
      [ATTR_NAME]:   state.pointName || '',
      [ATTR_ADDR]:   state.pointAddr || '',
    };
    $$('input[data-attr]', root).forEach(input => {
      const key = input.dataset.attr;
      if (key in map) input.value = map[key];
    });
  }

  function syncSummary(root, state) {
    const summary = $('[data-point-summary]', root);
    if (!summary) return;
    if (state.pointId) {
      summary.hidden = false;
      const nameEl = $('[data-point-name]', summary);
      const addrEl = $('[data-point-addr]', summary);
      const idEl   = $('[data-point-id]', summary);
      if (nameEl) nameEl.textContent = state.pointName || '';
      if (addrEl) addrEl.textContent = state.pointAddr || '';
      if (idEl)   idEl.textContent   = state.pointId ? `Kod: ${state.pointId}` : '';
    } else {
      summary.hidden = true;
    }
  }

  function syncCardSelection(root, method) {
    $$('.ship-card', root).forEach(card => {
      card.classList.toggle('is-selected', card.dataset.method === method);
    });
  }

  function syncPickupVisibility(root, method) {
    const row = $('[data-pickup-row]', root);
    if (!row) return;
    const needsPoint = METHODS_NEED_POINT.includes(method);
    row.hidden = !needsPoint;
    $$('[data-pickup-panel]', root).forEach(panel => {
      panel.hidden = panel.dataset.pickupPanel !== method;
    });
  }

  function syncCheckoutGuard(root, state) {
    const needsPoint = METHODS_NEED_POINT.includes(state.method);
    const missingPoint = needsPoint && !state.pointId;
    const missingMethod = !state.method;
    const blocked = missingMethod || missingPoint;
    const checkoutBtn = document.querySelector('#checkout, button[name="checkout"]');
    if (checkoutBtn) {
      checkoutBtn.disabled = blocked;
      checkoutBtn.dataset.shippingBlocked = blocked ? '1' : '';
      // Visually indicate the missing requirement
      if (missingMethod && !checkoutBtn.dataset.origText) {
        checkoutBtn.dataset.origText = checkoutBtn.textContent.trim();
      }
      if (checkoutBtn.dataset.origText) {
        checkoutBtn.textContent = missingMethod ? 'Wybierz sposób dostawy ↑' : (missingPoint ? 'Wybierz punkt odbioru ↑' : checkoutBtn.dataset.origText);
      }
    }
    const hint = $('[data-needs-point]', root);
    if (hint) hint.hidden = !missingPoint;
    // Show method-required hint
    let methodHint = $('[data-needs-method]', root);
    if (!methodHint && missingMethod) {
      methodHint = document.createElement('p');
      methodHint.className = 'ship-pickup__hint';
      methodHint.setAttribute('data-needs-method', '');
      methodHint.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="8" cy="8" r="7"/><path d="M8 5v3.5M8 11.2v.3"/></svg> Wybierz sposób dostawy, aby kontynuować.';
      const grid = $('.ship-grid', root);
      if (grid && grid.parentNode) grid.parentNode.insertBefore(methodHint, grid.nextSibling);
    }
    if (methodHint) methodHint.hidden = !missingMethod;
  }

  // ---------- Leaflet map ----------

  let leafletReady = false;
  let leafletLoadPromise = null;

  function preloadLeaflet() {
    if (leafletLoadPromise) return leafletLoadPromise;
    loadCSS(LEAFLET.css);
    leafletLoadPromise = loadJS(LEAFLET.js).then(() => {
      if (!window.L) throw new Error('window.L missing after load');
      delete window.L.Icon.Default.prototype._getIconUrl;
      window.L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl:       'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:     'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png',
      });
      leafletReady = true;
      return window.L;
    });
    return leafletLoadPromise;
  }

  function makePaczkomatIcon(L, isSelected = false) {
    const color = isSelected ? '#A8624B' : '#1A2341';
    const size = isSelected ? 34 : 30;
    const html = `<div style="
      width: ${size}px; height: ${size}px;
      background: ${color};
      border: 2px solid #B8925A;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.35);
    "><span style="
      transform: rotate(45deg);
      color: #B8925A;
      font-weight: 700;
      font-size: ${Math.round(size * 0.45)}px;
      font-family: Inter, sans-serif;
    ">P</span></div>`;
    return L.divIcon({
      html,
      iconSize: [size, size],
      iconAnchor: [size / 2, size],
      popupAnchor: [0, -size],
      className: 'shipx-marker-icon',
    });
  }

  let mapInstance = null;
  let markerLayer = null;
  let markersByName = new Map();
  let currentPoints = [];

  function getOrInitMap() {
    if (mapInstance) {
      setTimeout(() => mapInstance.invalidateSize(), 50);
      return mapInstance;
    }
    const L = window.L;
    const mount = $('[data-geo-mount]', document);
    if (!mount || !L) return null;
    mount.innerHTML = '';
    const container = document.createElement('div');
    container.style.width = '100%';
    container.style.height = '100%';
    mount.appendChild(container);

    mapInstance = L.map(container, { zoomControl: true }).setView(POLAND_CENTER, POLAND_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(mapInstance);
    markerLayer = L.layerGroup().addTo(mapInstance);

    // invalidate on resize (mobile rotation, modal opens etc.)
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        setTimeout(() => mapInstance && mapInstance.invalidateSize(), 80);
      });
      ro.observe(container);
    }
    window.addEventListener('resize', () => mapInstance && mapInstance.invalidateSize());
    setTimeout(() => mapInstance.invalidateSize(), 250);
    return mapInstance;
  }

  function renderMarkers(points, onSelect) {
    if (!markerLayer || !window.L) return;
    const L = window.L;
    markerLayer.clearLayers();
    markersByName = new Map();
    currentPoints = points;
    if (!points.length) return;
    const bounds = [];
    points.forEach(p => {
      if (!p.location) return;
      const ll = [p.location.latitude, p.location.longitude];
      bounds.push(ll);
      const marker = L.marker(ll, { icon: makePaczkomatIcon(L, false) });
      const a1 = (p.address && p.address.line1) || '';
      const a2 = (p.address && p.address.line2) || '';
      const html = `
        <div style="font-family: Inter, sans-serif; min-width: 220px;">
          <div style="font-weight: 700; color: #1A2341; font-size: 14px; margin-bottom: 4px;">
            Paczkomat ${escapeHtml(p.name)}
          </div>
          <div style="font-size: 13px; color: #4A5273; margin-bottom: 8px;">
            ${escapeHtml(a1)}<br>${escapeHtml(a2)}
          </div>
          <button type="button" data-pick="${escapeHtml(p.name)}" style="
            background: #B8925A; color: white; border: none;
            padding: 8px 14px; font-size: 13px; font-weight: 600;
            cursor: pointer; border-radius: 2px; width: 100%;
          ">Wybierz ten paczkomat</button>
        </div>`;
      marker.bindPopup(html);
      marker.on('popupopen', () => {
        const btn = document.querySelector(`button[data-pick="${CSS.escape(p.name)}"]`);
        if (btn) btn.addEventListener('click', () => onSelect(p), { once: true });
      });
      marker.addTo(markerLayer);
      markersByName.set(p.name, marker);
    });
    if (bounds.length === 1) mapInstance.setView(bounds[0], 14);
    else if (bounds.length) mapInstance.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
  }

  function renderList(points, onSelect) {
    const ul = $('[data-geo-list-items]', document);
    const head = $('[data-geo-list-head]', document);
    if (!ul) return;
    ul.innerHTML = '';
    if (head) {
      head.textContent = points.length
        ? `Znaleziono ${points.length} paczkomat${points.length === 1 ? '' : 'ów'}`
        : 'Brak wyników — spróbuj inny kod pocztowy lub miasto';
    }
    points.forEach(p => {
      const a1 = (p.address && p.address.line1) || '';
      const a2 = (p.address && p.address.line2) || '';
      const li = document.createElement('li');
      li.className = 'ship-geo-list-item';
      li.dataset.name = p.name;
      li.innerHTML = `
        <div class="ship-geo-list-item__name">Paczkomat ${escapeHtml(p.name)}</div>
        <div class="ship-geo-list-item__dist">${escapeHtml(formatDistance(p.distance || 0))}</div>
        <div class="ship-geo-list-item__addr">${escapeHtml(a1)}${a2 ? ', ' + escapeHtml(a2) : ''}</div>
        <button type="button" class="ship-geo-list-item__btn">Wybierz</button>
      `;
      li.addEventListener('click', (e) => {
        if (e.target.classList.contains('ship-geo-list-item__btn')) {
          e.stopPropagation();
          onSelect(p);
          return;
        }
        // focus marker on map
        const m = markersByName.get(p.name);
        if (m && mapInstance) {
          mapInstance.setView(m.getLatLng(), 15);
          m.openPopup();
        }
        $$('.ship-geo-list-item').forEach(x => x.classList.remove('is-focused'));
        li.classList.add('is-focused');
      });
      ul.appendChild(li);
    });
  }

  function showSkeleton(show) {
    const skel = $('[data-geo-list-skel]', document);
    const ul = $('[data-geo-list-items]', document);
    if (skel) skel.hidden = !show;
    if (ul && show) ul.innerHTML = '';
  }

  async function searchAndRender(root, query, onSelect) {
    setStatus(root, '');
    showSkeleton(true);
    const head = $('[data-geo-list-head]', document);
    if (head) head.textContent = `Szukam: ${query}...`;
    try {
      let points = [];
      if (isPolishPostCode(query)) {
        const code = normalizePostCode(query);
        points = await shipxSearch({
          relative_post_code: code,
          max_distance: String(SEARCH_RADIUS_M),
        }, `postcode:${code}`);
      } else if (query) {
        points = await shipxSearch({ city: query }, `city:${query.toLowerCase()}`);
      }
      showSkeleton(false);
      renderMarkers(points, onSelect);
      renderList(points, onSelect);
    } catch (e) {
      if (e.name === 'AbortError') return; // ignore — superseded by new search
      console.error('[shipping-picker] shipx search:', e);
      showSkeleton(false);
      if (head) head.textContent = 'Błąd pobierania. Wpisz kod ręcznie w polu poniżej mapy.';
    }
  }

  function openGeoModal() {
    const modal = $('[data-geo-modal]', document);
    if (!modal) return;
    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', '');
    // re-invalidate map after modal sized
    setTimeout(() => mapInstance && mapInstance.invalidateSize(), 150);
  }
  function closeGeoModal() {
    const modal = $('[data-geo-modal]', document);
    if (!modal) return;
    if (typeof modal.close === 'function') modal.close();
    else modal.removeAttribute('open');
  }

  // ---------- init ----------

  function initPicker(root) {
    const state = {
      method:    root.querySelector('input[name="ship_method_radio"]:checked')?.value || '',
      pointId:   $('input[data-attr="pickup_point_id"]', root)?.value || '',
      pointName: $('input[data-attr="pickup_point_name"]', root)?.value || '',
      pointAddr: $('input[data-attr="pickup_point_address"]', root)?.value || '',
    };

    function applyState({ persist = true } = {}) {
      syncCardSelection(root, state.method);
      syncPickupVisibility(root, state.method);
      syncHiddenInputs(root, state);
      syncSummary(root, state);
      syncCheckoutGuard(root, state);
      if (persist) {
        pushAttributes({
          [ATTR_METHOD]: state.method,
          [ATTR_ID]:     state.pointId,
          [ATTR_NAME]:   state.pointName,
          [ATTR_ADDR]:   state.pointAddr,
        }).then(() => setStatus(root, ''))
          .catch(err => {
            console.error('[shipping-picker]', err);
            setStatus(root, 'Nie udało się zapisać wyboru — odśwież stronę.', 'error');
          });
      }
    }

    function pickPoint(p) {
      state.pointId   = p.name;
      state.pointName = `Paczkomat ${p.name}`;
      const a1 = (p.address && p.address.line1) || '';
      const a2 = (p.address && p.address.line2) || '';
      state.pointAddr = [a1, a2].filter(Boolean).join(', ');
      applyState();
      closeGeoModal();
    }

    // radio change → also preload Leaflet jeśli to paczkomat (szybsze otwarcie mapy)
    $$('input[name="ship_method_radio"]', root).forEach(radio => {
      radio.addEventListener('change', () => {
        state.method = radio.value;
        if (!METHODS_NEED_POINT.includes(state.method)) {
          state.pointId = state.pointName = state.pointAddr = '';
        }
        applyState();
        if (state.method === 'paczkomat-inpost') {
          preloadLeaflet().catch(() => {});
          // also prefetch default search results
          shipxSearch({
            relative_post_code: DEFAULT_QUERY,
            max_distance: String(SEARCH_RADIUS_M),
          }, `postcode:${DEFAULT_QUERY}`).catch(() => {});
        }
      });
    });

    // jeśli paczkomat już wybrany przy load — preload od razu
    if (state.method === 'paczkomat-inpost') {
      preloadLeaflet().catch(() => {});
      shipxSearch({
        relative_post_code: DEFAULT_QUERY,
        max_distance: String(SEARCH_RADIUS_M),
      }, `postcode:${DEFAULT_QUERY}`).catch(() => {});
    }

    // manual code/name input
    $$('[data-manual-input]', root).forEach(input => {
      let timer = null;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const method = input.dataset.manualInput;
          if (state.method !== method) return;
          const val = input.value.trim();
          if (!val) {
            state.pointId = state.pointName = state.pointAddr = '';
          } else {
            state.pointId = val.toUpperCase().replace(/\s+/g, '');
            state.pointName = method === 'paczkomat-inpost'
              ? `Paczkomat ${state.pointId}`
              : val;
            state.pointAddr = '';
          }
          applyState();
        }, 350);
      });
    });

    // open map modal
    const geoBtn = $('[data-open-geowidget]', root);
    if (geoBtn) {
      geoBtn.addEventListener('click', async () => {
        try {
          await preloadLeaflet();
          openGeoModal();
          getOrInitMap();
          await searchAndRender(root, DEFAULT_QUERY, pickPoint);
        } catch (err) {
          console.error('[shipping-picker] init map:', err);
          setStatus(root, 'Nie udało się załadować mapy. Wpisz kod paczkomatu ręcznie poniżej.', 'error');
        }
      });
    }

    // search inside modal
    const searchInput = $('[data-modal-search]', document);
    const searchBtn = $('[data-modal-search-btn]', document);
    const doSearch = () => {
      const q = (searchInput?.value || '').trim();
      if (!q) return;
      searchAndRender(root, q, pickPoint);
    };
    if (searchBtn) searchBtn.addEventListener('click', doSearch);
    if (searchInput) {
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); doSearch(); }
      });
    }

    // geolokalizacja
    const geoLocBtn = $('[data-modal-geoloc]', document);
    if (geoLocBtn) {
      geoLocBtn.addEventListener('click', () => {
        if (!navigator.geolocation) return;
        geoLocBtn.disabled = true;
        showSkeleton(true);
        const head = $('[data-geo-list-head]', document);
        if (head) head.textContent = 'Pobieram lokalizację...';
        navigator.geolocation.getCurrentPosition(async (pos) => {
          geoLocBtn.disabled = false;
          try {
            const points = await shipxSearch({
              relative_point: `${pos.coords.latitude},${pos.coords.longitude}`,
              max_distance: '5000',
            }, `geo:${pos.coords.latitude.toFixed(3)},${pos.coords.longitude.toFixed(3)}`);
            showSkeleton(false);
            renderMarkers(points, pickPoint);
            renderList(points, pickPoint);
            if (points.length && mapInstance) {
              mapInstance.setView([pos.coords.latitude, pos.coords.longitude], 14);
            }
          } catch (e) {
            showSkeleton(false);
            if (head) head.textContent = 'Geolokalizacja zadziałała, ale wyszukiwanie się nie udało.';
          }
        }, () => {
          geoLocBtn.disabled = false;
          showSkeleton(false);
          const head = $('[data-geo-list-head]', document);
          if (head) head.textContent = 'Geolokalizacja niedostępna. Wpisz kod pocztowy lub miasto.';
        }, { timeout: 8000 });
      });
    }

    // close modal
    const closeBtn = $('[data-close-geo]', document);
    if (closeBtn) closeBtn.addEventListener('click', closeGeoModal);

    // clear selection
    const clearBtn = $('[data-clear-point]', root);
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        state.pointId = state.pointName = state.pointAddr = '';
        $$('[data-manual-input]', root).forEach(i => { i.value = ''; });
        applyState();
      });
    }

    applyState({ persist: false });
  }

  document.addEventListener('DOMContentLoaded', () => {
    $$('[data-shipping-picker]').forEach(initPicker);
  });
})();
